import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

import { HoverHintDefinitionGrid } from "@/components/ui/HoverHintDefinitionGrid";
import { buildDocumentPeekRows } from "@/lib/documentPeekRows";
import { formatQuantityWithUnit } from "@/lib/mrpSkuUnitDisplay";
import {
  formatReceiptDiscrepancyDate,
  formatReceiptDiscrepancyQuantity,
  receiptStatusLabel,
} from "@/pages/accounting/receiptDiscrepancyApi";
import { fetchInboundReceiptReview } from "@/pages/shipping/inboundApi";

export function inboundReceiptPeekQueryKey(
  receiptId: string,
): readonly ["shipping", "inbound-receipt-review", string] {
  return ["shipping", "inbound-receipt-review", receiptId];
}

function receivedDetail(quantity: string, unitOfMeasure: string | null): string {
  const parsed = Number(quantity);
  const quantityText = Number.isFinite(parsed) ? formatReceiptDiscrepancyQuantity(parsed) : quantity.trim();
  return formatQuantityWithUnit(quantityText, unitOfMeasure);
}

export function InboundReceiptDetailHoverHint({ receiptId }: { receiptId: string }): JSX.Element {
  const detailQuery = useQuery({
    queryKey: inboundReceiptPeekQueryKey(receiptId),
    queryFn: () => fetchInboundReceiptReview(receiptId),
    staleTime: 30_000,
  });

  if (detailQuery.isLoading) {
    return (
      <p className="flex items-center gap-1.5 text-xs text-slate-600">
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
        Loading receipt…
      </p>
    );
  }

  if (detailQuery.isError || detailQuery.data == null) {
    return <p className="text-xs text-slate-600">Could not load receipt preview.</p>;
  }

  const receipt = detailQuery.data;
  return (
    <HoverHintDefinitionGrid
      rows={buildDocumentPeekRows({
        header: [
          { label: "Receipt", value: receipt.id },
          { label: "Status", value: receiptStatusLabel(receipt.status) },
          { label: "Received", value: formatReceiptDiscrepancyDate(receipt.received_at) },
          { label: "Purchase order", value: receipt.source_number },
        ],
        lines: receipt.lines.map((line) => ({
          sku: line.sku,
          detail: receivedDetail(line.received_quantity, line.unit_of_measure),
        })),
      })}
    />
  );
}
